import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type { SectionSpecDetail } from "../models/SectionSpec";
import { getSectionSpec } from "../repositories/SectionSpecRepository";

type OnError = (error: Error) => void;

export const useSectionSpec = (
	sectionId: number | null,
	assetId: number,
	onError?: OnError,
) =>
	useQuery<SectionSpecDetail, Error>({
		queryKey: ["section-spec", sectionId, assetId],
		meta: { realtime: ["DIM.GetProfileSpec"] },
		queryFn: ({ signal }) => getSectionSpec({ sectionId, assetId }, signal),
		enabled: sectionId !== null,
		onError,
	});

// Keep the existing current/exact-ID hooks unchanged for configuration screens.
export const useSectionSpecAtTime = (
	sectionId: number | null,
	assetId: number,
	inspectedAt: Date | null,
) => {
	// Date fields emit valid intermediate dates while typing. Do not clear
	// readings for a transient revision the user has not finished selecting.
	const inspectionTime =
		inspectedAt && Number.isFinite(inspectedAt.getTime())
			? inspectedAt.getTime()
			: null;
	const [settledTime, setSettledTime] = useState(inspectionTime);
	useEffect(() => {
		const timer = setTimeout(() => setSettledTime(inspectionTime), 300);
		return () => clearTimeout(timer);
	}, [inspectionTime]);
	const isInspectionTimeSettled = inspectionTime === settledTime;
	const query = useQuery<SectionSpecDetail, Error>({
		queryKey: [
			"section-spec",
			sectionId,
			assetId,
			"inspection-time",
			inspectionTime,
		],
		meta: { realtime: ["DIM.GetProfileSpec"] },
		enabled:
			sectionId !== null && inspectionTime !== null && isInspectionTimeSettled,
		retry: false,
		keepPreviousData: true,
		queryFn: ({ signal }) =>
			getSectionSpec({ sectionId, assetId, inspectedAt }, signal),
	});
	return {
		...query,
		// Previous revisions can remain visible while resolving another sample time,
		// but a different section must never inherit their references or dimensions.
		data:
			query.isPreviousData && query.data?.spec?.SectionId !== sectionId
				? undefined
				: query.data,
		isInspectionTimeSettled,
	};
};

export const useSectionSpecVersion = (
	profileSpecId: number | null,
	onError?: OnError,
) =>
	useQuery<SectionSpecDetail, Error>({
		queryKey: ["section-spec-version", profileSpecId],
		meta: { realtime: ["DIM.GetProfileSpec"] },
		queryFn: ({ signal }) => getSectionSpec({ profileSpecId }, signal),
		enabled: profileSpecId !== null,
		onError,
	});
